import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db, auth } from "../firebase-config";
import {
  doc,
  getDoc,
  getDocs,
  updateDoc,
  collection,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { sendPasswordResetEmail } from "firebase/auth";
import "./EditEmployeePage.css";

function EditEmployeePage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [email, setEmail] = useState("");
  const [selectedSites, setSelectedSites] = useState([]);
  const [sites, setSites] = useState([]);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [hasWorkingHours, setHasWorkingHours] = useState(false);
  const [weeklySchedule, setWeeklySchedule] = useState({});
  const [toleranceDebut, setToleranceDebut] = useState(0);
  const [toleranceFin, setToleranceFin] = useState(0);
  const [startWeekDay, setStartWeekDay] = useState("Lundi");

  const [entreprise, setEntreprise] = useState("");
  const [siret, setSiret] = useState("");

  const days = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

  useEffect(() => {
    const fetchEmployee = async () => {
      try {
        console.log("Fetching employee with ID:", id);
        const ref = doc(db, "employees", id);
        const snap = await getDoc(ref);
        
        if (!snap.exists()) {
          throw new Error("Employé non trouvé");
        }

        const data = snap.data();
        console.log("Employee data:", data);
        
        setNom(data.nom || "");
        setPrenom(data.prenom || "");
        setEmail(data.email || "");
        setSelectedSites(Array.isArray(data.siteDeTravail) ? data.siteDeTravail : [data.siteDeTravail]);
        setHasWorkingHours(!!data.hasWorkingHours);
        setWeeklySchedule(data.weeklySchedule || {});
        setToleranceDebut(data.toleranceDebut || 0);
        setToleranceFin(data.toleranceFin || 0);
        setStartWeekDay(data.startWeekDay || "Lundi");
        setEntreprise(data.entreprise || "");
        setSiret(data.siret || "");
      } catch (err) {
        console.error("Erreur lors de la récupération de l'employé:", err);
        setError(err.message);
        throw err;
      }
    };

    const loadData = async () => {
      try {
        setLoading(true);
        await Promise.all([fetchEmployee(), fetchSites()]);
      } catch (err) {
        console.error("Erreur lors du chargement des données:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [id]);

  const fetchSites = async () => {
    try {
      console.log("Fetching sites");
      const snap = await getDocs(collection(db, "sites"));
      const list = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      console.log("Sites loaded:", list);
      setSites(list);
    } catch (err) {
      console.error("Erreur lors de la récupération des sites:", err);
      setError(err.message);
      throw err;
    }
  };

  const handleSiteChange = (e) => {
    const options = e.target.options;
    const selected = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selected.push(options[i].value);
      }
    }
    setSelectedSites(selected);
  };

  const handleScheduleChange = (day, field, value) => {
    setWeeklySchedule((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value,
      },
    }));
  };

  const handleSendResetEmail = async () => {
    try {
      await sendPasswordResetEmail(auth, email);
      setResetEmailSent(true);
      alert("Un email de réinitialisation du mot de passe a été envoyé à " + email);
    } catch (error) {
      console.error("Erreur lors de l'envoi de l'email :", error);
      alert("Erreur lors de l'envoi de l'email : " + error.message);
    }
  };

  const handleUpdate = async () => {
    if (!nom || !prenom || selectedSites.length === 0) {
      alert("Tous les champs sont obligatoires.");
      return;
    }

    try {
      const ref = doc(db, "employees", id);
      const employeeDoc = await getDoc(ref);
      const currentData = employeeDoc.data();
      
      // Vérifier si les sites ont changé
      const currentSites = Array.isArray(currentData.siteDeTravail) 
        ? currentData.siteDeTravail 
        : [currentData.siteDeTravail];
      
      const sitesChanged = JSON.stringify(currentSites.sort()) !== JSON.stringify(selectedSites.sort());

      // Mettre à jour l'employé
      await updateDoc(ref, {
        nom,
        prenom,
        email,
        siteDeTravail: selectedSites,
        hasWorkingHours,
        weeklySchedule,
        toleranceDebut,
        toleranceFin,
        startWeekDay,
        entreprise,
        siret,
      });

      // Si les sites ont changé, enregistrer dans l'historique
      if (sitesChanged) {
        await addDoc(collection(db, "sitesHistory"), {
          employeeEmail: email,
          oldSites: currentSites,
          newSites: selectedSites,
          changeDate: serverTimestamp(),
        });
      }

      alert("Salarié modifié avec succès !");
      navigate("/admin/employees");
    } catch (error) {
      console.error("Erreur lors de la modification :", error);
      alert("Erreur lors de la modification : " + error.message);
    }
  };

  if (loading) {
    return <div className="loading">Chargement en cours...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">Erreur : {error}</div>
        <button onClick={() => navigate("/admin/employees")}>Retour à la liste</button>
      </div>
    );
  }

  return (
    <div className="edit-employee-page">
      <h1>Modifier un salarié</h1>

      <input
        type="text"
        placeholder="Nom"
        value={nom}
        onChange={(e) => setNom(e.target.value)}
      />
      <input
        type="text"
        placeholder="Prénom"
        value={prenom}
        onChange={(e) => setPrenom(e.target.value)}
      />

      <div className="form-group">
        <label>Entreprise</label>
        <input type="text" value={entreprise} onChange={e => setEntreprise(e.target.value)} maxLength={64} placeholder="Nom de l'entreprise (facultatif)" />
      </div>
      <div className="form-group">
        <label>SIRET</label>
        <input type="text" value={siret} onChange={e => setSiret(e.target.value)} maxLength={14} placeholder="Numéro SIRET (facultatif)" />
      </div>

      <div className="password-section">
        <button 
          type="button" 
          onClick={handleSendResetEmail}
          className="reset-password-button"
          disabled={resetEmailSent}
        >
          {resetEmailSent ? "✓ Email de réinitialisation envoyé" : "🔑 Réinitialiser le mot de passe"}
        </button>
      </div>

      <label>Sites de travail :</label>
      <select
        multiple
        value={selectedSites}
        onChange={handleSiteChange}
        className="multi-select"
      >
        {sites.map((site) => (
          <option key={site.id} value={site.id}>
            {site.nom} ({site.rayon} m)
          </option>
        ))}
      </select>

      <div className="horaires-section">
        <div className="infos-explicatives">
          <p><strong>🕑 Horaires :</strong> définissez les horaires de travail pour chaque jour de la semaine.</p>
          <p><strong>⏱️ Tolérances :</strong> définissez une marge de tolérance en minutes :</p>
          <p style={{ fontStyle: "italic", fontSize: "0.9em" }}>
            • Tolérance début : si le salarié arrive dans cette marge avant l'heure prévue, le temps n'est pas compté. Au-delà, tout est compté.<br />
            • Tolérance fin : si le salarié part dans cette marge après l'heure prévue, le temps n'est pas compté. Au-delà, tout est compté.
          </p>
        </div>

        <label>
          <input
            type="checkbox"
            checked={hasWorkingHours}
            onChange={() => {
              setHasWorkingHours(!hasWorkingHours);
              if (!hasWorkingHours) {
                setToleranceDebut(0);
                setToleranceFin(0);
              }
            }}
          />
          Activer les horaires de travail
        </label>

        {hasWorkingHours && (
          <div className="horaires-table">
            <table>
              <thead>
                <tr>
                  <th>Jour</th>
                  <th>Heure Début</th>
                  <th>Heure Fin</th>
                </tr>
              </thead>
              <tbody>
                {days.map((day) => (
                  <tr key={day}>
                    <td>{day}</td>
                    <td>
                      <input
                        type="time"
                        value={weeklySchedule[day]?.debut || ""}
                        onChange={(e) => handleScheduleChange(day, "debut", e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="time"
                        value={weeklySchedule[day]?.fin || ""}
                        onChange={(e) => handleScheduleChange(day, "fin", e.target.value)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="tolerances">
              <h4>Tolérances</h4>
              <label>
                Tolérance début (minutes) :
                <input
                  type="number"
                  min="0"
                  value={toleranceDebut}
                  onChange={(e) => setToleranceDebut(Number(e.target.value))}
                />
              </label>
              <label>
                Tolérance fin (minutes) :
                <input
                  type="number"
                  min="0"
                  value={toleranceFin}
                  onChange={(e) => setToleranceFin(Number(e.target.value))}
                />
              </label>
            </div>
          </div>
        )}
      </div>

      <div className="buttons-container">
        <button onClick={handleUpdate} className="save-button">💾 Enregistrer</button>
        <button onClick={() => navigate("/admin/employees")} className="cancel-button">Annuler</button>
      </div>
    </div>
  );
}

export default EditEmployeePage;
